// Differentiated Time Log — constants (lanes, codes, colors, grid geometry).
//
// CODES: CANON — ratified by PO 2026-07-24 via Delphi panel
// (docs/delphi_activity_codes_result.json). 39 ops / 442 sub-codes,
// IADC-DDR+/WITSML-aligned. The canonical activity-code library is
// OPERATION_CODES below: a 2-level code1→code2 model, each op tagged with its
// lane + lifecyclePhase, keyed on the COMPOSITE (code1, code2) because code2 is
// NOT globally unique. Per DESIGN §6.1 the library is bound at well creation and
// snapshotted onto every entry via CODING_TEMPLATE_ID/VERSION. The flat
// CODE1_OPTIONS and the per-lane picklists below sit alongside it as the current
// field-entry presets.

import type { Lane } from "./types";
import { JOB_REPORT_TYPES } from "../../constants";
import { routeForReportTypeWithSub } from "../jobs/reportRoute";

// Generic timeline geometry + lane-height constants now live in the shared
// @geothermal/timeline-core package; re-exported here so every existing
// `../constants` import site is untouched during the extraction.
export {
  HOURS_PER_DAY,
  MS_PER_HOUR,
  MS_PER_DAY,
  WINDOW_PAD_HOURS,
  WINDOW_HOURS,
  WINDOW_MS,
  DAY_BOUNDARY_HOUR,
  SNAP_MINUTES,
  DEFAULT_BLOCK_HOURS,
  LANE_HEIGHT,
  CRITICAL_LANE_HEIGHT,
  LANE_LABEL_WIDTH,
  AXIS_HEIGHT,
} from "@geothermal/timeline-core";

// Time code library snapshot stamped on every entry. Selected at bore creation
// (alongside the cost library + CRS + basis — DESIGN §6.1). Defaults to the
// canonical IADC-DDR+ library (OPERATION_CODES); operator/custom libraries plug
// in the same shape. DRILLING-SPECIFIC — stays in the feature.
//
// ID is UNCHANGED ("iadc-ddr-plus-v1") on purpose: it is the stable library
// IDENTITY every already-stamped time-log entry references (codingTemplates/
// seeds.ts) — bumping it would orphan them. VERSION is bumped "1" → "2" to mark
// the ratified-canon generation stamped on NEW entries (canonMigrationNotes §6).
export const CODING_TEMPLATE_ID = "iadc-ddr-plus-v1";
export const CODING_TEMPLATE_VERSION = "2";

// ── Default drilling lanes ──────────────────────────────────────────────────
// Critical Path is CENTERED; off-critical lanes stack above AND below it so the
// spine reads as central (PO directive 2026-06-26). Weather pinned to the top,
// Inspections to the bottom (PO directive 2026-06-26).
export const DEFAULT_DRILLING_LANES: readonly Lane[] = [
  {
    id: "lane-weather",
    label: "Weather",
    type: "workstream",
    isCriticalPath: false,
    position: "above",
    // Sentinel: the "above" band sorts DESCENDING (higher order = further from the spine = higher on
    // screen), so a large order pins Weather to the very TOP above every other above-band lane
    // (incl. the prebake extras Wireline/Logs order 4, Mud order 3) — the PO "Weather pinned to top".
    order: 100,
  },
  {
    id: "lane-mwd",
    label: "MWD",
    type: "workstream",
    isCriticalPath: false,
    position: "above",
    order: 1,
  },
  {
    id: "lane-mob",
    label: "Mobilization",
    type: "workstream",
    isCriticalPath: false,
    position: "above",
    order: 0,
  },
  {
    id: "lane-critical",
    label: "Critical Path",
    type: "workstream",
    isCriticalPath: true,
    position: "critical",
    order: 0,
  },
  {
    id: "lane-zonal",
    label: "Zonal Isolation",
    type: "workstream",
    isCriticalPath: false,
    position: "below",
    order: 0,
  },
  {
    id: "lane-bop",
    label: "BOP Testing",
    type: "workstream",
    isCriticalPath: false,
    position: "below",
    order: 1,
  },
  {
    id: "lane-inspections",
    label: "Inspections",
    type: "workstream",
    isCriticalPath: false,
    position: "below",
    order: 2,
  },
] as const;

/** The standard roster — these lanes are NOT user-deletable. */
export const DEFAULT_LANE_IDS: ReadonlySet<string> = new Set(
  DEFAULT_DRILLING_LANES.map((l) => l.id),
);

/** True for a built-in lane; false for a user-added one (which can be deleted). */
export function isDefaultLane(laneId: string): boolean {
  return DEFAULT_LANE_IDS.has(laneId);
}

/** Preset lanes a user can add beyond the defaults (picklist + custom entry). */
export const ADDABLE_LANE_PRESETS: readonly string[] = [
  "Cementing",
  "Casing / Tubular",
  "Wireline / Logging",
  "Mud / Fluids",
  "Logistics / Crane",
  "Inspection / Testing",
] as const;

// ── Operational codes ───────────────────────────────────────────────────────
export interface CodeOption {
  value: string;
  label: string;
}

// ── CANON activity-code library (39 ops / 442 sub-codes) ────────────────────
// SOURCE OF TRUTH for activity codes. Ratified by PO 2026-07-24 via the Delphi
// panel (docs/delphi_activity_codes_result.json → final.mainCodes). Structure
// chosen (canonMigrationNotes §1): ONE canonical registry `OPERATION_CODES`,
// each entry a MainOp { code, label, lane, lifecyclePhase, subCodes }. The
// legacy exports (CRITICAL_PATH_MAIN/SUB) are DERIVED from it so existing call
// sites keep compiling; the workover/completion/intervention/P&A ops become
// reachable per job class via operationsForPhase()/operationsForLane().
//
// ⚠ COMPOSITE KEY (REQUIRED). code2 is NOT globally unique — 12 sub-codes live
// under multiple parents (e.g. STUCK_PIPE ∈ FISHING|NPT, BULLHEAD ∈
// WELL_CONTROL|PUMPING_SERVICES, SET_BRIDGE_PLUG ∈ DOWNHOLE_TOOLS|PLUG_ABANDON,
// SET_CIBP ∈ DOWNHOLE_TOOLS|PLUG_ABANDON, SIDEWALL_CORE ∈ CORING|WIRELINE_LOGS,
// SHIFT_SLEEVE ∈ DOWNHOLE_TOOLS|SLICKLINE, SECTION_MILL ∈ MILLING|PLUG_ABANDON,
// WEIGHT_UP ∈ MUD_FLUIDS|WELL_CONTROL, MONITOR ∈ WELL_CONTROL|PUMPING_SERVICES,
// FLOWBACK ∈ WELL_TEST|STIMULATION, LOST_CIRC ∈ CIRC_COND|NPT, WOW ∈ WAIT|NPT).
// Consumers MUST key every lookup on the pair (code1, code2), never code2 alone.
// Use resolveSubCode / labelForOpCode / subCodesForOperation below.

/** Lifecycle phase a job runs in; drives which lanes/ops render for that job
 *  class. "universal" ops are available in every phase. */
export type LifecyclePhase =
  | "drilling"
  | "completion"
  | "workover"
  | "intervention"
  | "p&a"
  | "universal";

/** The home swim-lane of an operation. universal/npt/safety/weather render on
 *  every phase. */
export type OperationLane =
  | "critical"
  | "mwd"
  | "zonal"
  | "bop"
  | "mud"
  | "logs"
  | "completion"
  | "workover"
  | "intervention"
  | "pna"
  | "mob"
  | "safety"
  | "weather"
  | "npt"
  | "inspections"
  | "universal";

/** One main operation (code1) with its lane, lifecycle phase, and code2 subs. */
export interface MainOp {
  code: string;
  label: string;
  lane: OperationLane;
  lifecyclePhase: LifecyclePhase;
  subCodes: readonly CodeOption[];
}

export const OPERATION_CODES: readonly MainOp[] = [
  {
    code: "DRILLING",
    label: "Drilling",
    lane: "critical",
    lifecyclePhase: "drilling",
    subCodes: [
      { value: "ROTARY", label: "Rotary Drilling" },
      { value: "SLIDE", label: "Slide / Directional Drilling" },
      { value: "RSS", label: "Rotary-Steerable Drilling" },
      { value: "CONNECTION", label: "Connection" },
      { value: "DRILL_CEMENT", label: "Drill Cement / Shoe / Float" },
      { value: "DRILL_OUT", label: "Drill Out Plug / Bridge Plug" },
      { value: "DRILL_OFF", label: "Drill-Off / ROP Test" },
      { value: "HOLD_MAINTAIN", label: "Hold / Maintain Angle" },
      { value: "PILOT_HOLE", label: "Pilot Hole" },
      { value: "AIR_DRILL", label: "Air / Underbalanced Drilling" },
      { value: "MPD", label: "Managed Pressure Drilling (MPD)" },
      { value: "KICKOFF", label: "Kick Off / Sidetrack" },
      { value: "MUD_LOG", label: "Mud Logging (Gas & Cuttings, surface)" },
    ],
  },
  {
    code: "CORING",
    label: "Coring",
    lane: "critical",
    lifecyclePhase: "drilling",
    subCodes: [
      { value: "CUT_CORE", label: "Cut Core" },
      { value: "CORE_TRIP", label: "Trip for Core" },
      { value: "RETRIEVE_CORE", label: "Retrieve / Recover Core" },
      { value: "REAM_CORE", label: "Ream to Coring Point" },
      { value: "ORIENT_CORE", label: "Orient Core" },
      { value: "SIDEWALL_CORE", label: "Sidewall Core (mechanical)" },
      { value: "HANDLE_CORE", label: "Handle / Box Core" },
    ],
  },
  {
    code: "REAMING",
    label: "Reaming / Hole Opening",
    lane: "critical",
    lifecyclePhase: "drilling",
    subCodes: [
      { value: "REAM_BACK", label: "Ream Back" },
      { value: "BACK_REAM", label: "Back-Ream" },
      { value: "WASH_REAM", label: "Wash & Ream" },
      { value: "UNDER_REAM", label: "Under-Ream" },
      { value: "HOLE_OPEN", label: "Hole Opening / Enlarging" },
      { value: "REAM_CEMENT", label: "Ream Cement" },
      { value: "DRESS_OFF", label: "Dress Off / Repass Section" },
    ],
  },
  {
    code: "SURVEY_DIR",
    label: "Survey / Directional",
    lane: "mwd",
    lifecyclePhase: "drilling",
    subCodes: [
      { value: "MWD_SURVEY", label: "MWD Survey" },
      { value: "GYRO_SURVEY", label: "Gyro Survey" },
      { value: "SINGLE_SHOT", label: "Single-Shot Survey" },
      { value: "MULTISHOT", label: "Multishot Survey" },
      { value: "DRIFT_SURVEY", label: "Drift Survey (inclination-only)" },
      { value: "ORIENT_TF", label: "Orient Tool Face" },
      { value: "DIR_STEER", label: "Directional Steering / Downlink" },
      { value: "DEFINITIVE_SURVEY", label: "Definitive Survey QC" },
    ],
  },
  {
    code: "CIRC_COND",
    label: "Circulate & Condition",
    lane: "critical",
    lifecyclePhase: "universal",
    subCodes: [
      { value: "CIRCULATE", label: "Circulate" },
      { value: "BOTTOMS_UP", label: "Circulate Bottoms-Up" },
      { value: "COND_MUD", label: "Condition Mud" },
      { value: "DISPLACE", label: "Displace / Change Fluid" },
      { value: "SPOT_PILL", label: "Spot Pill / Sweep" },
      { value: "LOST_CIRC", label: "Lost Circulation Treatment" },
      { value: "LCM", label: "Pump LCM" },
      { value: "SLOW_PUMP", label: "Slow-Pump-Rate Test (SCR)" },
      { value: "ESTABLISH_CIRC", label: "Establish / Regain Circulation" },
      { value: "HOLE_FILL", label: "Fill / Monitor Hole" },
    ],
  },
  {
    code: "MUD_FLUIDS",
    label: "Mud / Fluids Management",
    lane: "mud",
    lifecyclePhase: "universal",
    subCodes: [
      { value: "MIX_MUD", label: "Mix / Build Mud" },
      { value: "TREAT_MUD", label: "Treat / Chemical Addition" },
      { value: "WEIGHT_UP", label: "Weight Up (Planned)" },
      { value: "THIN_MUD", label: "Thin / Dilute" },
      { value: "TRANSFER", label: "Transfer / Accumulate Fluid" },
      { value: "SOLIDS_CONTROL", label: "Solids Control / Shakers" },
      { value: "MUD_TEST", label: "Mud Properties Test" },
      { value: "DISPLACE_MUD", label: "Displace to New System" },
      { value: "DUMP_MUD", label: "Dump / Dispose / Clean Pits" },
      { value: "MIX_LCM", label: "Mix LCM" },
      { value: "DISPLACE_BRINE", label: "Displace to Completion Brine" },
      { value: "PACKER_FLUID", label: "Spot Packer Fluid" },
      { value: "FILTER_BRINE", label: "Load / Filter Completion Brine" },
    ],
  },
  {
    code: "TRIPS",
    label: "Tripping / Pipe Handling",
    lane: "critical",
    lifecyclePhase: "universal",
    subCodes: [
      { value: "RIH", label: "Run In Hole" },
      { value: "POOH", label: "Pull Out of Hole" },
      { value: "RIH_BHA", label: "Run BHA / Tool In Hole" },
      { value: "POOH_BHA", label: "Pull BHA / Tool Out of Hole" },
      { value: "MAKE_UP", label: "Make Up / Pick Up" },
      { value: "LAY_DOWN", label: "Lay Down" },
      { value: "SHORT_TRIP", label: "Short Trip" },
      { value: "WIPER_TRIP", label: "Wiper Trip" },
      { value: "FLOW_CHECK", label: "Flow Check" },
      { value: "TRIP_TANK", label: "Trip-Tank Monitor" },
      { value: "PUMP_OUT", label: "Pump Out of Hole" },
      { value: "BIT_CHANGE", label: "Bit / Tool Change" },
    ],
  },
  {
    code: "RUN_CASING",
    label: "Run Casing / Tubulars",
    lane: "zonal",
    lifecyclePhase: "drilling",
    subCodes: [
      { value: "RUN_CSG", label: "Run Casing" },
      { value: "CSG_MAKEUP", label: "Make Up (Torque-Turn)" },
      { value: "LAND_CSG", label: "Land Casing / Set Slips" },
      { value: "RUN_LINER", label: "Run Liner" },
      { value: "SET_LINER_HANGER", label: "Set Liner Hanger" },
      { value: "RUN_TIEBACK", label: "Run Tieback" },
      { value: "RUN_CONDUCTOR", label: "Run / Drive Conductor" },
      { value: "RUN_FLOAT", label: "Run Float Equipment / Shoe Track" },
      { value: "CENTRALIZE", label: "Centralize / Run Accessories" },
      { value: "WASH_REAM_CSG", label: "Wash & Ream Casing" },
      { value: "FILL_CIRC", label: "Fill & Circulate Casing" },
      { value: "RECIPROCATE", label: "Reciprocate / Rotate Casing" },
    ],
  },
  {
    code: "CEMENTING",
    label: "Cementing",
    lane: "zonal",
    lifecyclePhase: "universal",
    subCodes: [
      { value: "RU_CEMENT", label: "Rig Up / Down Cementing" },
      { value: "MIX_SLURRY", label: "Mix / Batch Slurry" },
      { value: "PRIMARY_CEMENT", label: "Primary Cement" },
      { value: "LINER_CEMENT", label: "Liner Cement" },
      { value: "DISPLACE_CEMENT", label: "Displace Cement" },
      { value: "BUMP_PLUG", label: "Bump Plug" },
      { value: "SQUEEZE", label: "Squeeze Cement" },
      { value: "CEMENT_PLUG", label: "Balanced / Isolation Cement Plug" },
      { value: "WOC", label: "Wait on Cement (WOC / Fraguar)" },
      { value: "DRILL_OUT_SHOE", label: "Drill Out Shoe / Float" },
      { value: "TOP_JOB", label: "Top-Up / Remedial Cement" },
      { value: "DUMP_BAIL", label: "Dump Bailer" },
      {
        value: "CBL_EVAL",
        label: "Cement Evaluation Review (interpret / decision)",
      },
    ],
  },
  {
    code: "INTEGRITY_TEST",
    label: "Casing / Formation Integrity Test",
    lane: "zonal",
    lifecyclePhase: "universal",
    subCodes: [
      { value: "CSG_TEST", label: "Casing Pressure Test" },
      { value: "FIT", label: "Formation Integrity Test (FIT)" },
      { value: "LOT", label: "Leak-Off Test (LOT)" },
      { value: "XLOT", label: "Extended LOT / Minifrac" },
      { value: "SHOE_TEST", label: "Shoe Test" },
      { value: "LINER_TOP_TEST", label: "Liner-Top Test" },
      { value: "INFLOW_TEST", label: "Inflow / Negative Test" },
      { value: "MIT", label: "Mechanical Integrity Test (MIT)" },
      { value: "ANNULUS_TEST", label: "Annulus / Leak Test" },
    ],
  },
  {
    code: "BOP_WELLHEAD",
    label: "BOP / Wellhead",
    lane: "bop",
    lifecyclePhase: "universal",
    subCodes: [
      { value: "NU_BOP", label: "Nipple Up BOP" },
      { value: "ND_BOP", label: "Nipple Down BOP" },
      { value: "STUMP_TEST", label: "Stump Test" },
      { value: "TEST_LOW", label: "Low-Pressure Test" },
      { value: "TEST_HIGH", label: "High-Pressure Test" },
      { value: "BOP_TEST_CSG", label: "Test on Casing" },
      { value: "FUNCTION_TEST", label: "Function / Actuation Test" },
      { value: "CHANGE_RAMS", label: "Change / Repair Rams & Elements" },
      { value: "INSTALL_WELLHEAD", label: "Install / Service Wellhead" },
      { value: "INSTALL_HANGER", label: "Install Casing / Tubing Hanger" },
      { value: "INSTALL_TREE", label: "Install / Nipple Up Tree" },
      { value: "TEST_TREE", label: "Test Christmas Tree" },
      { value: "WEAR_BUSHING", label: "Install / Retrieve Wear Bushing" },
      { value: "INSTALL_RCD", label: "Install RCD / Diverter" },
    ],
  },
  {
    code: "WIRELINE_LOGS",
    label: "Wireline / LWD Logging",
    lane: "logs",
    lifecyclePhase: "universal",
    subCodes: [
      { value: "RU_WL", label: "Rig Up / Down Wireline" },
      { value: "WL_RIH", label: "Run In Hole" },
      { value: "WL_POOH", label: "Pull Out of Hole" },
      { value: "OPENHOLE_LOG", label: "Open-Hole Logging" },
      { value: "CASEDHOLE_LOG", label: "Cased-Hole Logging" },
      { value: "CBL_VDL", label: "Cement Bond Log Run (CBL/VDL)" },
      { value: "PRODUCTION_LOG", label: "Production Logging (PLT)" },
      { value: "CORRELATION_LOG", label: "GR/CCL/Neutron Correlation" },
      { value: "VSP_CHECKSHOT", label: "VSP / Check-Shot" },
      { value: "SIDEWALL_CORE", label: "Sidewall Coring (wireline)" },
      { value: "LWD", label: "LWD Acquisition / Memory Dump" },
      { value: "TOOLSTRING", label: "Tool-String Make-Up / Ops" },
      { value: "WL_FISH", label: "Wireline Fishing" },
    ],
  },
  {
    code: "WELL_TEST",
    label: "Well Test / Formation Evaluation",
    lane: "logs",
    lifecyclePhase: "universal",
    subCodes: [
      { value: "DST", label: "Drill-Stem Test (DST)" },
      { value: "WL_FORM_TEST", label: "Wireline Formation Test (RFT/MDT)" },
      { value: "SWAB_TEST", label: "Swab Test (Pistoneo / Inducción)" },
      { value: "FLOW_TEST", label: "Flow / Production Test (Surgencia)" },
      { value: "BUILDUP", label: "Pressure Build-Up / Fall-Off" },
      { value: "INJECTION_TEST", label: "Injectivity / Admission Test" },
      { value: "STEP_RATE", label: "Step-Rate Test" },
      { value: "FLOWBACK", label: "Flowback / Clean-Up" },
      { value: "CHOKE_ADJUST", label: "Choke Adjustment / Bean-Up" },
      { value: "SEPARATOR_TEST", label: "Separator / Multiphase Test" },
      { value: "BHP_GAUGE", label: "Bottom-Hole Pressure / Gradient Gauge" },
      { value: "SAMPLING", label: "Fluid Sampling" },
    ],
  },
  {
    code: "FISHING",
    label: "Fishing",
    lane: "critical",
    lifecyclePhase: "universal",
    subCodes: [
      { value: "DIAGNOSE", label: "Diagnose / Free-Point" },
      { value: "BACKOFF", label: "Back-Off" },
      { value: "JARRING", label: "Jarring" },
      { value: "MILLING", label: "Milling (Fish / Junk)" },
      { value: "WASHOVER", label: "Washover" },
      { value: "OVERSHOT", label: "Overshot / Spear" },
      { value: "JUNK_BASKET", label: "Junk Basket / Magnet / Junk Run" },
      { value: "CUT_PIPE", label: "Cut Pipe / String" },
      { value: "RETRIEVE_FISH", label: "Retrieve Fish" },
      { value: "STUCK_PIPE", label: "Stuck-Pipe Ops" },
      { value: "FISH_TRIP", label: "Trip Fishing Tools" },
    ],
  },
  {
    code: "WELL_CONTROL",
    label: "Well Control",
    lane: "critical",
    lifecyclePhase: "universal",
    subCodes: [
      { value: "SHUT_IN", label: "Shut In (Hard / Soft)" },
      { value: "MONITOR", label: "Monitor / Flow Check" },
      { value: "RECORD_PRESSURES", label: "Record SIDPP / SICP" },
      { value: "DRILLERS", label: "Driller's Method" },
      { value: "WAIT_WEIGHT", label: "Wait & Weight / Engineer's" },
      { value: "CONCURRENT", label: "Concurrent Method" },
      { value: "WEIGHT_UP", label: "Weight Up Kill Mud" },
      { value: "CIRCULATE_KILL", label: "Circulate Out Influx" },
      { value: "BULLHEAD", label: "Bullhead" },
      { value: "VOLUMETRIC", label: "Volumetric / Lubricate & Bleed (kill)" },
      { value: "STRIPPING", label: "Strip / Snub Pipe" },
      { value: "BARITE_PLUG", label: "Barite / Cement Plug" },
      { value: "DYNAMIC_KILL", label: "Dynamic Kill" },
      { value: "DIVERTING", label: "Diverting" },
      { value: "BLEED_DOWN", label: "Bleed Down / Depressurize (routine)" },
    ],
  },
  {
    code: "PERFORATING",
    label: "Perforating",
    lane: "completion",
    lifecyclePhase: "completion",
    subCodes: [
      { value: "RU_PERF", label: "Rig Up Perforating" },
      { value: "RUN_GUNS", label: "Run Guns" },
      { value: "GUN_MAKEUP", label: "Gun Arming / Make-Up" },
      { value: "CORRELATE", label: "Depth Correlation" },
      { value: "FIRE_GUNS", label: "Fire / Shoot" },
      { value: "TCP", label: "Tubing-Conveyed Perforating" },
      { value: "WIRELINE_PERF", label: "Wireline / E-line Perforating" },
      { value: "THROUGH_TUBING_PERF", label: "Through-Tubing Perforating" },
      { value: "POOH_GUNS", label: "Pull / Recover Guns" },
      { value: "PLUG_N_PERF", label: "Plug-and-Perf Stage" },
      { value: "PERF_WASH", label: "Perforation Wash / Breakdown" },
      { value: "MISFIRE", label: "Recover / Handle Misfire" },
      { value: "REPERF", label: "Re-Perforate" },
    ],
  },
  {
    code: "RUN_COMPLETION",
    label: "Run / Pull Completion String",
    lane: "completion",
    lifecyclePhase: "completion",
    subCodes: [
      { value: "RUN_TUBING", label: "Run Production Tubing" },
      { value: "PULL_TUBING", label: "Pull Tubing" },
      { value: "MAKEUP_TBG", label: "Make Up / Test Tubing" },
      { value: "RUN_PACKER", label: "Run Packer" },
      { value: "RUN_SCSSV", label: "Run Subsurface Safety Valve" },
      { value: "RUN_SSD", label: "Run Sliding Sleeve (SSD)" },
      { value: "RUN_PBR_SEAL", label: "Run PBR / Seal Assembly" },
      { value: "RUN_NIPPLE", label: "Run Landing Nipple / Flow Coupling" },
      { value: "RUN_MANDREL", label: "Run Gas-Lift Mandrel" },
      { value: "SPACE_OUT", label: "Space Out" },
      { value: "LAND_HANGER", label: "Land Tubing Hanger" },
      { value: "DRIFT_TALLY", label: "Drift / Tally Tubing" },
      { value: "PRESSURE_TEST_TBG", label: "Pressure-Test Tubing" },
      { value: "COMMISSION", label: "Commission / Bring On Line" },
    ],
  },
  {
    code: "DOWNHOLE_TOOLS",
    label: "Set / Retrieve Downhole Tools",
    lane: "completion",
    lifecyclePhase: "universal",
    subCodes: [
      { value: "SET_PACKER", label: "Set Packer" },
      { value: "RELEASE_PACKER", label: "Release / Retrieve Packer" },
      { value: "TEST_PACKER", label: "Test Packer" },
      { value: "SET_BRIDGE_PLUG", label: "Set Bridge Plug" },
      { value: "PULL_BRIDGE_PLUG", label: "Retrieve Bridge Plug" },
      { value: "SET_CIBP", label: "Set CIBP" },
      { value: "SET_PLUG", label: "Set Plug (Tapon)" },
      { value: "RETRIEVE_PLUG", label: "Retrieve / Fish Plug" },
      { value: "SET_RETAINER", label: "Set Cement Retainer" },
      { value: "SET_STANDING_VALVE", label: "Set / Pull Standing Valve" },
      { value: "SET_STRADDLE", label: "Set Straddle" },
      { value: "SET_ANCHOR", label: "Set Tubing Anchor" },
      { value: "SHIFT_SLEEVE", label: "Shift Sliding Sleeve" },
      { value: "PROFILE_WORK", label: "Nipple / Profile Work" },
      { value: "SET_WHIPSTOCK", label: "Set / Orient Whipstock" },
      { value: "RETRIEVE_WHIPSTOCK", label: "Retrieve Whipstock" },
    ],
  },
  {
    code: "SAND_CONTROL",
    label: "Sand Control / Gravel Pack",
    lane: "completion",
    lifecyclePhase: "completion",
    subCodes: [
      { value: "RUN_SCREENS", label: "Run Sand Screens" },
      { value: "GRAVEL_PACK", label: "Gravel Pack" },
      { value: "FRAC_PACK", label: "Frac Pack" },
      { value: "OPENHOLE_GP", label: "Open-Hole Gravel Pack" },
      { value: "RUN_GP_PACKER", label: "Run GP Packer / Crossover" },
      { value: "SET_GP", label: "Set / Squeeze Pack" },
      { value: "PLACE_PROPPANT", label: "Place Gravel / Proppant" },
      { value: "SAND_PLUG", label: "Place Sand Plug" },
      { value: "REVERSE_OUT", label: "Reverse Out Excess" },
      { value: "RESIN_CONSOL", label: "Resin Consolidation" },
    ],
  },
  {
    code: "STIMULATION",
    label: "Stimulation / Fracturing",
    lane: "completion",
    lifecyclePhase: "completion",
    subCodes: [
      { value: "RU_STIM", label: "Rig Up / Down Stimulation" },
      { value: "PRESSURE_TEST_LINES", label: "Pressure-Test Lines" },
      { value: "BREAKDOWN", label: "Formation Breakdown" },
      { value: "INJECTIVITY_TEST", label: "Injectivity / Step-Rate Test" },
      { value: "MINIFRAC_DFIT", label: "Minifrac / DFIT" },
      { value: "MATRIX_ACID", label: "Matrix Acidizing" },
      { value: "ACID_FRAC", label: "Acid Fracturing" },
      { value: "ACID_WASH", label: "Acid Wash / Soak" },
      { value: "HYDRAULIC_FRAC", label: "Hydraulic Fracturing" },
      { value: "PAD_STAGE", label: "Pad Stage" },
      { value: "PROPPANT_STAGE", label: "Proppant Stage" },
      { value: "DIVERTER", label: "Diverter / Ball-Out" },
      { value: "FLUSH", label: "Flush / Displace to Perfs" },
      { value: "SCREENOUT", label: "Handle Screen-Out" },
      {
        value: "WELLHEAD_ISOLATION",
        label: "Wellhead Isolation Tool / Frac Tree",
      },
      { value: "FLOWBACK", label: "Flowback" },
    ],
  },
  {
    code: "ARTIFICIAL_LIFT",
    label: "Artificial Lift Install / Pull",
    lane: "completion",
    lifecyclePhase: "universal",
    subCodes: [
      { value: "RUN_RODS", label: "Run Sucker Rods" },
      { value: "PULL_RODS", label: "Pull Sucker Rods" },
      { value: "RUN_INSERT_PUMP", label: "Run Rod / Insert Pump" },
      { value: "PULL_INSERT_PUMP", label: "Pull Rod / Insert Pump" },
      { value: "SET_PUMP", label: "Seat / Set Downhole Pump" },
      { value: "RUN_ESP", label: "Run ESP (BES)" },
      { value: "PULL_ESP", label: "Pull ESP" },
      { value: "RUN_PCP", label: "Run / Pull PCP" },
      { value: "RUN_GAS_LIFT", label: "Run / Service Gas Lift" },
      { value: "SET_GL_VALVE", label: "Set / Change Gas-Lift Valve" },
      { value: "RUN_PLUNGER", label: "Install Plunger Lift" },
      { value: "SPACE_PUMP", label: "Space Out Pump / Rods" },
      { value: "POLISH_ROD", label: "Polished Rod / Stuffing Box" },
      { value: "RUN_INJECTION", label: "Run Injection String" },
      { value: "COMMISSION_LIFT", label: "Commission / Start Lift System" },
    ],
  },
  {
    code: "WELL_CLEANOUT",
    label: "Wellbore Cleanout",
    lane: "workover",
    lifecyclePhase: "workover",
    subCodes: [
      { value: "TAG_FILL", label: "Tag Fill / PBTD" },
      { value: "WASH_SAND", label: "Wash Sand / Frac Sand" },
      { value: "CLEAN_FILL", label: "Clean Out Fill / Drill Fill" },
      { value: "SCRAPE_CSG", label: "Scrape / Brush Casing" },
      { value: "JET_WASH", label: "Jet Wash / Wash to Bottom" },
      { value: "BAIL_SAND", label: "Bail Fill / Sand Bailer" },
      { value: "SCALE_MILL", label: "Mill / Remove Scale" },
      { value: "PARAFFIN", label: "Paraffin / Wax Cut (Mechanical)" },
      { value: "JUNK_CLEAN", label: "Junk / Debris Cleanout" },
      { value: "REVERSE_CIRC", label: "Reverse Circulate / Circulate Clean" },
      { value: "CLEAN_TO_PBTD", label: "Clean Out to PBTD" },
    ],
  },
  {
    code: "MILLING",
    label: "Milling / Section Milling",
    lane: "workover",
    lifecyclePhase: "workover",
    subCodes: [
      { value: "MILL_PLUG", label: "Mill Plug / Retainer" },
      { value: "MILL_PACKER", label: "Mill Packer" },
      { value: "MILL_CEMENT", label: "Mill Cement" },
      { value: "MILL_CASING", label: "Mill Casing" },
      { value: "SECTION_MILL", label: "Section Mill (Window)" },
      { value: "MILL_WINDOW", label: "Mill Window (Sidetrack)" },
      { value: "DRESS_TOP", label: "Dress / Clean Top" },
    ],
  },
  {
    code: "COILED_TUBING",
    label: "Coiled Tubing",
    lane: "intervention",
    lifecyclePhase: "intervention",
    subCodes: [
      { value: "RU_CT", label: "Rig Up / Down CT Unit" },
      { value: "CT_RIH", label: "Run In Hole" },
      { value: "CT_POOH", label: "Pull Out of Hole" },
      { value: "CT_CIRC", label: "Circulate / Wash" },
      { value: "CT_CLEANOUT", label: "Wellbore / Sand Cleanout" },
      { value: "CT_MILL", label: "Mill / Drill Out" },
      { value: "CT_ACID", label: "Acid / Chemical Placement" },
      { value: "CT_N2", label: "Nitrogen Lift / Kickoff" },
      { value: "CT_FISH", label: "CT Fishing" },
      { value: "CT_LOG", label: "CT-Conveyed Logging" },
      { value: "CT_SET_PLUG", label: "Set / Pull Plug" },
      { value: "CT_PERF", label: "Perforating Conveyance" },
    ],
  },
  {
    code: "SLICKLINE",
    label: "Slickline / Mechanical Wireline",
    lane: "intervention",
    lifecyclePhase: "intervention",
    subCodes: [
      { value: "RU_SL", label: "Rig Up / Down Slickline" },
      { value: "SL_RIH", label: "Run In Hole" },
      { value: "SL_POOH", label: "Pull Out of Hole" },
      { value: "SET_PLUG_SL", label: "Set Plug / Standing Valve" },
      { value: "PULL_PLUG_SL", label: "Pull Plug" },
      { value: "GAUGE_RUN", label: "Gauge / Drift Run" },
      { value: "SHIFT_SLEEVE", label: "Shift Sliding Sleeve" },
      { value: "GLV_CHANGE", label: "Gas-Lift Valve Change" },
      { value: "MEMORY_GAUGE", label: "Run / Retrieve Memory Gauge" },
      { value: "BAILER", label: "Bailer Run" },
      { value: "SL_FISH", label: "Slickline Fishing" },
    ],
  },
  {
    code: "SNUBBING",
    label: "Snubbing / Hydraulic Workover",
    lane: "intervention",
    lifecyclePhase: "intervention",
    subCodes: [
      { value: "RU_SNUB", label: "Rig Up / Down Snubbing (HWO)" },
      { value: "SNUB_IN", label: "Snub In Under Pressure" },
      { value: "SNUB_OUT", label: "Snub Out Under Pressure" },
      { value: "STRIP_PIPE", label: "Strip Pipe" },
      { value: "PRESSURE_CONTROL", label: "Pressure-Control Ops" },
    ],
  },
  {
    code: "PUMPING_SERVICES",
    label: "Pumping / Well Kill Services",
    lane: "intervention",
    lifecyclePhase: "intervention",
    subCodes: [
      { value: "KILL_WELL", label: "Kill Well (Ahogar)" },
      { value: "LOAD_HOLE", label: "Load Hole" },
      { value: "BULLHEAD", label: "Bullhead" },
      { value: "CIRC_KILL", label: "Circulate / Reverse Kill" },
      { value: "SPOT_KILL_PILL", label: "Spot Kill Pill" },
      { value: "HOT_OIL", label: "Hot Oil / Hot Water" },
      { value: "SCALE_TREAT", label: "Scale Treatment" },
      { value: "PARAFFIN_CUT", label: "Paraffin / Wax Treatment" },
      { value: "CHEM_TREAT", label: "Chemical Treatment" },
      { value: "MONITOR", label: "Monitor Well / Static Check" },
    ],
  },
  {
    code: "SURFACE_HOOKUP",
    label: "Surface Hook-Up / Flowline Tie-In",
    lane: "completion",
    lifecyclePhase: "completion",
    subCodes: [
      {
        value: "FLOWLINE_TIE_IN",
        label: "Flowline Tie-In / Tee & Lateral (Tee)",
      },
      {
        value: "PRODUCTION_BRIDGE",
        label: "Production Bridge (Puente de Produccion)",
      },
      { value: "MANIFOLD_HOOKUP", label: "Manifold / Battery Hook-Up" },
      {
        value: "WELLSITE_FACILITY",
        label: "Wellsite Facility / Separator Hook-Up",
      },
      { value: "WELL_COMMISSION", label: "Bring Well On Line / Commission" },
    ],
  },
  {
    code: "PLUG_ABANDON",
    label: "Plug & Abandonment (P&A)",
    lane: "pna",
    lifecyclePhase: "p&a",
    subCodes: [
      { value: "SET_CEMENT_PLUG", label: "Set Balanced Cement Plug" },
      { value: "SET_BRIDGE_PLUG", label: "Set Bridge Plug (Base)" },
      { value: "SET_CIBP", label: "Set CIBP" },
      { value: "TAG_TEST_PLUG", label: "Tag / Pressure-Test Plug" },
      { value: "PERF_SQUEEZE", label: "Perforate & Squeeze" },
      {
        value: "PERF_WASH_CEMENT",
        label: "Perf-Wash-Cement (Annular Barrier)",
      },
      { value: "SECTION_MILL", label: "Section Mill (Rock-to-Rock)" },
      { value: "CUT_CASING", label: "Cut Casing" },
      { value: "PULL_CASING", label: "Recover / Pull Casing" },
      { value: "RETRIEVE_TUBING", label: "Retrieve Tubing / Completion" },
      { value: "VERIFY_BARRIER", label: "Verify Barrier / Well Integrity" },
      { value: "REMOVE_WELLHEAD", label: "Cut / Recover Wellhead" },
      { value: "SURFACE_PLUG", label: "Surface / Environmental Plug" },
      { value: "SUSPEND", label: "Temporary Suspension" },
      { value: "SITE_RESTORE", label: "Site Restoration / Reclamation" },
    ],
  },
  {
    code: "RIG_MOVE",
    label: "Rig Move / Mob & Demob",
    lane: "mob",
    lifecyclePhase: "universal",
    subCodes: [
      { value: "MOVE_IN", label: "Move In / Transport" },
      { value: "MOVE_OUT", label: "Move Out / Demob" },
      { value: "SPOT_RIG", label: "Spot Rig & Substructure" },
      { value: "RIG_UP", label: "Rig Up" },
      { value: "RIG_DOWN", label: "Rig Down" },
      { value: "SKID_WALK", label: "Skid / Walk Rig (Pad)" },
      { value: "RU_MAST", label: "Raise / Lower Mast" },
      { value: "TRANSPORT", label: "Road Transport / Trucking" },
      { value: "RU_SERVICE", label: "Rig Up Service Unit / Company" },
      { value: "RD_SERVICE", label: "Rig Down Service Unit" },
      { value: "RU_POWER", label: "Rig Up Power / Mud System" },
      { value: "PREP_LOCATION", label: "Location Prep" },
      {
        value: "CLEAN_WELLHEAD_CELLAR",
        label: "Clean / Prep Wellhead & Cellar (canonical)",
      },
      { value: "HOOK_UP_UTIL", label: "Hook Up Utilities" },
      { value: "PRE_SPUD", label: "Pre-Spud Inspection / Checklist" },
    ],
  },
  {
    code: "TUBULAR_HANDLING",
    label: "Tubular / Tool Handling",
    lane: "mob",
    lifecyclePhase: "universal",
    subCodes: [
      { value: "MAKE_UP_BHA", label: "Make Up BHA / Tool" },
      { value: "BREAK_OUT_BHA", label: "Break Out BHA / Tool" },
      { value: "PICKUP_LAYDOWN", label: "Pick Up / Lay Down Pipe" },
      { value: "RACK_BACK", label: "Rack Back / Stow" },
      { value: "MAKE_BREAK", label: "Make / Break Connection" },
      {
        value: "HANDLE_TUBULARS",
        label: "Handle / Condition Materials (Maniobra / Estiba)",
      },
      { value: "CONDITION_TUBULAR", label: "Condition / Dope Tubulars" },
      { value: "PREP_TOOL", label: "Prepare / Make Up Tool" },
    ],
  },
  {
    code: "SVC_RIG",
    label: "Service / Maintain Rig",
    lane: "critical",
    lifecyclePhase: "universal",
    subCodes: [
      { value: "SCHED_MAINT", label: "Scheduled Maintenance" },
      { value: "SLIP_CUT", label: "Slip & Cut Drilling Line" },
      { value: "LUBRICATE", label: "Lubricate" },
      { value: "INSPECT", label: "Inspect" },
      { value: "CRANE_LIFT", label: "Crane / Forklift Lift" },
      { value: "HOUSEKEEP", label: "Housekeeping / Rig Wash" },
      { value: "RIG_INSPECT", label: "Rig Inspection" },
      { value: "CREW_HANDOVER", label: "Crew Change / Handover" },
    ],
  },
  {
    code: "REPAIR_RIG",
    label: "Repair Rig / Equipment",
    lane: "critical",
    lifecyclePhase: "universal",
    subCodes: [
      { value: "REP_TOPDRIVE", label: "Top Drive / Rotary" },
      { value: "REP_PUMP", label: "Mud / Service Pump" },
      { value: "REP_DRAWWORKS", label: "Drawworks / Hoisting" },
      { value: "REP_ELEC", label: "Electrical / VFD / Generators" },
      { value: "REP_MECH", label: "Mechanical" },
      { value: "REP_HYD", label: "Hydraulics" },
      { value: "REP_ROTARY", label: "Rotary Table / Tongs" },
      { value: "REP_INSTRUMENT", label: "Instrumentation / EDR / Controls" },
      { value: "REP_BOP", label: "BOP / Well-Control Equipment" },
      { value: "REP_SHAKERS", label: "Shakers / Solids Control" },
      { value: "REP_LINES", label: "Lines / Standpipe" },
      { value: "REP_SERVICE_EQUIP", label: "Service-Company Equipment" },
    ],
  },
  {
    code: "INSPECTION",
    label: "Inspection / Calibration",
    lane: "inspections",
    lifecyclePhase: "universal",
    subCodes: [
      { value: "DP_INSPECT", label: "Drillpipe Inspection" },
      { value: "CSG_INSPECT", label: "Casing / Tubing Inspection" },
      { value: "STRAP", label: "Strap / Measure" },
      { value: "DRIFT", label: "Drift" },
      { value: "TALLY", label: "Tally" },
      { value: "DEMAG", label: "Demag Pipe / Collars" },
      { value: "CLEAN_PIPE", label: "Clean / Dope Pipe" },
      { value: "BOP_INSP", label: "BOP Inspection" },
      { value: "RIG_INSP", label: "Rig Inspection" },
      { value: "CALIBRATION", label: "Sensor / Instrument Calibration" },
      { value: "THIRD_PARTY", label: "Third-Party Inspection" },
    ],
  },
  {
    code: "SAFETY_HSE",
    label: "Safety / HSE",
    lane: "safety",
    lifecyclePhase: "universal",
    subCodes: [
      { value: "SAFETY_MEETING", label: "Safety Meeting / Pre-Tour" },
      { value: "JSA_TAILGATE", label: "JSA / Tailgate / Toolbox" },
      { value: "BOP_DRILL", label: "Well-Control / BOP Drill" },
      { value: "EMERGENCY_DRILL", label: "Emergency / Evac / H2S Drill" },
      { value: "PPE_CHECK", label: "PPE / Equipment Check" },
      { value: "PERMIT", label: "Permit to Work" },
      { value: "INCIDENT", label: "Incident Response / Investigation" },
      { value: "SPILL_RESPONSE", label: "Spill / Leak Response" },
      { value: "STAND_DOWN", label: "Safety Stand-Down" },
      { value: "ENV_MONITOR", label: "Environmental Monitoring" },
      { value: "MEDICAL", label: "Medical / Emergency" },
    ],
  },
  {
    code: "WEATHER",
    label: "Weather",
    lane: "weather",
    lifecyclePhase: "universal",
    subCodes: [
      { value: "OBSERVATION", label: "Weather Observation (stream)" },
      {
        value: "WEATHER_STANDBY",
        label: "Weather Standby (-> WAIT.WOW / NPT.WOW)",
      },
      { value: "SECURE_FOR_WEATHER", label: "Secure Rig for Weather" },
    ],
  },
  {
    code: "WAIT",
    label: "Waiting On (Planned)",
    lane: "universal",
    lifecyclePhase: "universal",
    subCodes: [
      { value: "WOW", label: "Waiting on Weather" },
      { value: "WO_DAYLIGHT", label: "Waiting on Daylight" },
      { value: "WO_ORDERS", label: "Waiting on Orders / Decision" },
      { value: "WO_MATERIAL", label: "Waiting on Material" },
      { value: "WO_PERSONNEL", label: "Waiting on Personnel / Crew" },
      { value: "WO_EQUIPMENT", label: "Waiting on Equipment" },
      { value: "WO_CEMENT", label: "Waiting on Cement (WOC)" },
      { value: "WO_SERVICE", label: "Waiting on Service Company" },
      { value: "WO_LOCATION", label: "Waiting on Location / Roads" },
      { value: "WO_TRANSPORT", label: "Waiting on Transport / Crane" },
      { value: "WO_PRESSURE", label: "Waiting on Pressure / Stabilization" },
      { value: "WO_WELL", label: "Waiting on Well (Soak / Static)" },
      { value: "WO_PRODUCTION", label: "Waiting on Production / Reservoir" },
      { value: "WO_OTHER_WELL", label: "Standby - Other Well on Pad" },
      { value: "STANDBY", label: "General Standby / Suspended" },
    ],
  },
  {
    code: "NPT",
    label: "NPT (Non-Productive Time)",
    lane: "npt",
    lifecyclePhase: "universal",
    subCodes: [
      { value: "WOW", label: "Weather Downtime" },
      { value: "EQUIP_RIG", label: "Rig Equipment Failure (Contractor)" },
      {
        value: "EQUIP_3RD_PARTY",
        label: "3rd-Party / Service Equipment Failure",
      },
      { value: "DOWNHOLE_EQUIP", label: "Downhole Tool / BHA Failure" },
      { value: "STUCK_PIPE", label: "Stuck Pipe / Pack-Off" },
      { value: "LOST_CIRC", label: "Lost Circulation" },
      { value: "INFLUX_KICK", label: "Influx / Kick" },
      {
        value: "WELLBORE_INSTABILITY",
        label: "Wellbore Instability / Sloughing",
      },
      { value: "WELLBORE_OTHER", label: "Other Wellbore Problem" },
      { value: "FISHING_NPT", label: "Fishing (Lost Time)" },
      { value: "WELL_CONTROL_NPT", label: "Well-Control Event" },
      { value: "WAIT_MATERIAL", label: "Waiting on Material" },
      { value: "WAIT_PERSONNEL", label: "Waiting on Personnel" },
      { value: "WAIT_SERVICE", label: "Waiting on Service Company" },
      {
        value: "LABOR_COMMUNITY",
        label: "Labor / Union / Community (Gremio / Road Block)",
      },
      // PO 2026-07-26 — socio-political NPT is separately tracked in LatAm/W.Africa ops.
      { value: "CIVIL_UNREST", label: "Civil Unrest / Demonstrations" },
      { value: "SECURITY", label: "Security / Armed Threat" },
      { value: "ROAD_ACCESS", label: "Road / Location Access" },
      { value: "POWER_LOSS", label: "Power / Utility Loss" },
      { value: "HSE_STANDDOWN", label: "HSE Stand-Down" },
      { value: "HSE_INCIDENT", label: "Incident / Investigation" },
      { value: "REWORK", label: "Rework / Do-Over" },
      { value: "OTHER", label: "Other NPT" },
    ],
  },
  {
    code: "OTHER",
    label: "Other / General",
    lane: "universal",
    lifecyclePhase: "universal",
    subCodes: [
      { value: "GENERIC_OP", label: "Generic Operation (recode by memo)" },
      { value: "HANDLING", label: "Material / Tubular Handling (uncoded)" },
      { value: "RIG_MISC", label: "Rig Miscellaneous" },
      { value: "ADMIN", label: "Administrative / Requisition" },
      { value: "MEETING", label: "Non-Safety Meeting / Coordination" },
      { value: "THIRD_PARTY_SVC", label: "Third-Party Service (uncoded)" },
      { value: "MISC", label: "Miscellaneous" },
    ],
  },
] as const;

/** code1 → MainOp. The composite-key base for every sub-code lookup. */
export const OPERATION_BY_CODE: ReadonlyMap<string, MainOp> = new Map(
  OPERATION_CODES.map((op) => [op.code, op]),
);

/** Every code1 (main-op) value in the canonical library, as CodeOptions. */
export const OPERATION_CODE1_OPTIONS: readonly CodeOption[] =
  OPERATION_CODES.map((op) => ({ value: op.code, label: op.label }));

/** Sub-codes for a main op, keyed by code1 (composite base). null when the op is
 *  unknown or has no subs. */
export function subCodesForOperation(
  code1: string,
): readonly CodeOption[] | null {
  const subs = OPERATION_BY_CODE.get(code1)?.subCodes;
  return subs && subs.length > 0 ? subs : null;
}

/** Resolve a composite (code1, code2) to its sub CodeOption. The ONLY correct
 *  way to look a sub-code up, since code2 is not globally unique. */
export function resolveSubCode(
  code1: string,
  code2: string,
): CodeOption | null {
  return (
    OPERATION_BY_CODE.get(code1)?.subCodes.find((s) => s.value === code2) ??
    null
  );
}

/** Composite-keyed display label for a (code1[, code2]) pair. */
export function labelForOpCode(code1: string, code2?: string): string {
  const op = OPERATION_BY_CODE.get(code1);
  if (!op) return code2 || code1;
  if (!code2) return op.label;
  return op.subCodes.find((s) => s.value === code2)?.label ?? code2;
}

/** Every canonical op that lives on a given lane. */
export function operationsForLane(lane: OperationLane): readonly MainOp[] {
  return OPERATION_CODES.filter((op) => op.lane === lane);
}

/** Ops applicable to a job of the given lifecycle phase: the phase-specific ops
 *  plus the universal ops available on every phase. This is the panel's
 *  "derive the visible ops from the job's lifecyclePhase" model. */
export function operationsForPhase(phase: LifecyclePhase): readonly MainOp[] {
  return OPERATION_CODES.filter(
    (op) => op.lifecyclePhase === phase || op.lifecyclePhase === "universal",
  );
}

/** The distinct lanes an op of this phase can occupy — the swim-lane roster a
 *  job class renders (canonMigrationNotes §3). Universal lanes are always in. */
export function lanesForPhase(phase: LifecyclePhase): readonly OperationLane[] {
  const seen = new Set<OperationLane>();
  for (const op of operationsForPhase(phase)) seen.add(op.lane);
  return [...seen];
}

/** The four PHASE-BRANDED lanes — each is the identity lane of ONE lifecycle
 *  phase and only ever renders on that phase's own timeline. A drilling timeline
 *  must never show an (empty) Completion / Workover / Intervention / P&A lane
 *  (PO 2026-08-04), even though some UNIVERSAL ops (e.g. artificial-lift,
 *  downhole-tools) home to the completion lane and would otherwise leak it into
 *  every phase via lanesForPhase(). Keyed by lane → the phase that owns it. */
export const PHASE_BRANDED_LANE_OWNER: Partial<
  Record<OperationLane, LifecyclePhase>
> = {
  completion: "completion",
  workover: "workover",
  intervention: "intervention",
  pna: "p&a",
};

/** The curated swim-lane roster a timeline of the given phase renders: every lane
 *  an op of this phase (or a universal op) can occupy, MINUS any phase-branded
 *  lane owned by a DIFFERENT phase. This is the "curate each timeline with the
 *  lanes relevant to its type" rule (PO 2026-08-04): a drilling timeline shows
 *  its drilling + universal service lanes (Weather/NPT/Safety/General/Mud/Logs/…)
 *  but NOT Completion/Workover/Intervention/P&A; a completion timeline keeps its
 *  own Completion lane and drops the other three brands; and so on. Universal
 *  service lanes (weather, npt, safety, universal) are unbranded and always in. */
export function lanesForTimeline(
  phase: LifecyclePhase,
): readonly OperationLane[] {
  return lanesForPhase(phase).filter((lane) => {
    const owner = PHASE_BRANDED_LANE_OWNER[lane];
    return owner === undefined || owner === phase;
  });
}

export const CODE1_OPTIONS: readonly CodeOption[] = [
  { value: "RIG_UP", label: "Rig Up / Move In" },
  { value: "DRILL", label: "Drilling (rotary / slide)" },
  { value: "TRIP", label: "Tripping (in / out)" },
  { value: "CIRC", label: "Circulate / Condition" },
  { value: "SURVEY", label: "Survey" },
  { value: "CASING", label: "Run Casing" },
  { value: "CEMENT", label: "Cementing" },
  { value: "BOP", label: "BOP (nipple up / test)" },
  { value: "WELL_CTRL", label: "Well Control" },
  { value: "LOG", label: "Logging (wireline / LWD)" },
  { value: "COMP", label: "Completion" },
  { value: "RIG_DOWN", label: "Rig Down / Move Out" },
  { value: "WAIT", label: "Waiting On" },
  { value: "NPT", label: "NPT (non-productive time)" },
  { value: "SAFETY", label: "Safety / HSE" },
  { value: "WEATHER", label: "Weather" },
] as const;

/** code2 for NPT = downtime reason; otherwise a generic sub-activity set. */
export const NPT_CODE2_OPTIONS: readonly CodeOption[] = [
  { value: "WOW", label: "Waiting on Weather" },
  { value: "EQUIP_RIG", label: "Rig Equipment Failure" },
  { value: "EQUIP_3RD_PARTY", label: "3rd-Party Equipment" },
  { value: "WELLBORE", label: "Wellbore (stuck / losses / influx)" },
  { value: "WAIT_MATERIAL", label: "Waiting on Material" },
  { value: "WAIT_PERSONNEL", label: "Waiting on Personnel" },
  { value: "HSE_STANDDOWN", label: "HSE Stand-down" },
  { value: "OTHER", label: "Other" },
] as const;

export const RESPONSIBLE_PARTY_OPTIONS: readonly string[] = [
  "Operator",
  "Drilling Contractor",
  "Service Company",
  "Third Party",
  "Weather / Force Majeure",
] as const;

// ── Per-lane task + milestone picklists ─────────────────────────────────────
// The PO-curated field-entry presets for the current 7-lane drilling roster
// (PO 2026-06-26). These sit ALONGSIDE the canonical OPERATION_CODES library as
// the hand-tuned per-lane picklists the Time Entry modal offers today; the full
// 39-op library is reached per job class via operationsForPhase/operationsForLane.
// Each standard lane carries its own task list; lanes without one fall back to
// the global CODE1_OPTIONS. Mobilization is fully populated (PO 2026-06-26):
// rig-up is mostly PARALLEL paths, so its value is the critical MILESTONES.

export const MOBILIZATION_TASKS: readonly CodeOption[] = [
  { value: "MOVE_IN", label: "Move-In / Rig Transport" },
  { value: "SPOT_RIG", label: "Spot Rig & Substructure" },
  { value: "RU_SUBSTRUCTURE", label: "Rig Up Substructure" },
  { value: "RU_DRAWWORKS", label: "Rig Up Drawworks" },
  { value: "RU_MAST", label: "Rig Up Mast / Derrick" },
  { value: "STRING_UP", label: "String Up Blocks & Lines" },
  { value: "RU_MUD_SYSTEM", label: "Rig Up Mud System" },
  { value: "RU_PUMPS", label: "Rig Up Pumps" },
  { value: "RU_POWER", label: "Rig Up Power / Generators" },
  { value: "RU_CONTROLS", label: "Rig Up Doghouse / Controls" },
  { value: "HOOK_UP_UTIL", label: "Hook Up Utilities" },
];

export const MOBILIZATION_MILESTONES: readonly CodeOption[] = [
  { value: "BACK_YARD_SET", label: "Back Yard Set" },
  { value: "DERRICK_RAISED", label: "Derrick Raised" },
  { value: "PRE_SPUD_CHECKLIST", label: "Pre-Spud Checklist" },
];

// Weather is NOT a task picklist — it's a structured observation stream
// (DESIGN §8). The Weather lane is special-cased to capture WeatherObservations.
export const WEATHER_LANE_ID = "lane-weather";

export const PRECIP_OPTIONS: readonly CodeOption[] = [
  { value: "none", label: "None" },
  { value: "rain", label: "Rain" },
  { value: "snow", label: "Snow" },
  { value: "sleet", label: "Sleet" },
  { value: "hail", label: "Hail" },
];

export const NATURAL_DISASTER_OPTIONS: readonly CodeOption[] = [
  { value: "none", label: "None" },
  { value: "tornado", label: "Tornado" },
  { value: "hurricane", label: "Hurricane" },
  { value: "tropical_storm", label: "Tropical Storm" },
  { value: "earthquake", label: "Earthquake" },
  { value: "flood", label: "Flood" },
  { value: "wildfire", label: "Wildfire" },
  { value: "blizzard", label: "Blizzard / Winter Storm" },
  { value: "dust_storm", label: "Dust / Sand Storm" },
  { value: "volcanic", label: "Volcanic Activity / Ashfall" },
  { value: "landslide", label: "Landslide / Mudslide" },
  { value: "other", label: "Other" },
];

export interface WeatherUnitLabels {
  precip: "in" | "cm";
  temp: "F" | "C";
  speed: "mph" | "kph";
  pressure: "inHg" | "hPa";
  distance: "mi" | "km";
}

/** Display unit labels for weather fields, driven by the global unit selector. */
export function weatherUnits(isMetric: boolean): WeatherUnitLabels {
  return isMetric
    ? { precip: "cm", temp: "C", speed: "kph", pressure: "hPa", distance: "km" }
    : {
        precip: "in",
        temp: "F",
        speed: "mph",
        pressure: "inHg",
        distance: "mi",
      };
}

// Inspections lane — inspections + prep + a Calibration sub-group (PO 2026-06-26).
// Two-level (main → sub) like the Critical Path. Calibration subs: TopDrive /
// Tank Volumes / Standpipe Pressure are PO-named; the remainder are the standard
// rig-sensor calibration set.
export const INSPECTION_TASKS: readonly CodeOption[] = [
  { value: "DRILLPIPE_INSP", label: "Drillpipe Inspection" },
  { value: "CASING_INSP", label: "Casing Inspection" },
  { value: "CASING_PREP", label: "Casing Prep" },
  { value: "DEMAG", label: "Demag Pipe / Collars" },
  { value: "RIG_INSP", label: "Rig Inspection" },
  { value: "CALIBRATION", label: "Calibration" },
];

export const INSPECTION_SUB: Record<string, readonly CodeOption[]> = {
  CASING_PREP: [
    { value: "STRAP", label: "Strap" },
    { value: "CLEAN", label: "Clean" },
    { value: "DRIFT", label: "Drift" },
  ],
  DEMAG: [
    { value: "DEMAG_PIPE", label: "Drill Pipe" },
    { value: "DEMAG_COLLARS", label: "Collars" },
  ],
  CALIBRATION: [
    { value: "CAL_TOPDRIVE", label: "Top Drive" },
    { value: "CAL_TANK_VOL", label: "Tank Volumes" },
    { value: "CAL_STANDPIPE", label: "Standpipe Pressure" },
    { value: "CAL_HOOKLOAD", label: "Hookload / Weight Indicator" },
    { value: "CAL_FLOW", label: "Flow Sensor / Paddle" },
    { value: "CAL_SPM", label: "Pump Strokes (SPM)" },
    { value: "CAL_TORQUE", label: "Torque" },
    { value: "CAL_RPM", label: "Rotary RPM" },
    { value: "CAL_PVT", label: "Pit Volume Totalizer (PVT)" },
    { value: "CAL_TRIPTANK", label: "Trip Tank" },
    { value: "CAL_GAS", label: "Gas Detector" },
    { value: "CAL_H2S", label: "H2S Monitor" },
  ],
};

// Zonal Isolation = cementing + the shoe integrity tests FIT/LOT (PO 2026-06-26).
export const ZONAL_ISOLATION_TASKS: readonly CodeOption[] = [
  { value: "RU_CEMENT", label: "Rig Up Cementing" },
  { value: "CEMENT_JOB", label: "Cement Casing" },
  { value: "BUMP_PLUG", label: "Bump Plug" },
  { value: "WOC", label: "Wait on Cement (WOC)" },
  { value: "DRILL_OUT_SHOE", label: "Drill Out Shoe / Float" },
  { value: "CSG_TEST", label: "Casing Pressure Test" },
  { value: "FIT", label: "Formation Integrity Test (FIT)" },
  { value: "LOT", label: "Leak-Off Test (LOT)" },
];

export const ZONAL_ISOLATION_MILESTONES: readonly CodeOption[] = [
  { value: "PLUG_BUMPED", label: "Plug Bumped" },
  { value: "WOC_COMPLETE", label: "WOC Complete" },
  { value: "FIT_LOT_ACHIEVED", label: "FIT / LOT Achieved" },
];

// BOP Testing — every activity is differentiated STUMP vs CASING. Actuation
// test is CASING-only (PO 2026-06-26).
export const BOP_TESTING_TASKS: readonly CodeOption[] = [
  // On the stump (during rig-up, before nippling up to the wellhead)
  { value: "STUMP_NU", label: "Stump — Nipple Up" },
  { value: "STUMP_PT_LOW", label: "Stump — Low Pressure Test" },
  { value: "STUMP_PT_HIGH", label: "Stump — High Pressure Test" },
  // On casing (after nippling up to the wellhead)
  { value: "CSG_NU", label: "Casing — Nipple Up" },
  { value: "CSG_PT_LOW", label: "Casing — Low Pressure Test" },
  { value: "CSG_PT_HIGH", label: "Casing — High Pressure Test" },
  { value: "CSG_ACTUATION", label: "Casing — Actuation Test" },
];

export const BOP_TESTING_MILESTONES: readonly CodeOption[] = [
  { value: "STUMP_TEST_PASSED", label: "Stump Test Passed" },
  { value: "BOP_NIPPLED_UP", label: "BOP Nippled Up (Casing)" },
  { value: "CSG_TEST_WITNESSED", label: "Casing Test Witnessed / Passed" },
];

// MWD (PO 2026-06-26: BHA Makeup, Survey). Milestones TBD in interview.
export const MWD_TASKS: readonly CodeOption[] = [
  { value: "BHA_MAKEUP", label: "BHA Makeup" },
  { value: "SURVEY", label: "Survey" },
];

export const MWD_MILESTONES: readonly CodeOption[] = [];

// Critical Path — the gating spine. ALWAYS starts with Spud, ends with Rig
// Release (PO 2026-06-26). The drilling day is anchored to Spud, not the first
// time entry (which is usually MOB).
export const SPUD_CODE = "SPUD";
export const RIG_RELEASE_CODE = "RIG_RELEASE";

// The Critical Path is the MOST structured lane: a two-level MAIN code → SUB
// code model, DERIVED from the canonical OPERATION_CODES library above so labels
// and sub-codes stay in lock-step with canon. code1 = main operation, code2 =
// activity sub-code. NPT's sub-codes remain the quick downtime-reason list
// (NPT_CODE2_OPTIONS); the full ratified NPT taxonomy lives on the NPT op in
// OPERATION_CODES. Spud / Rig Release are MILESTONES, not ops codes (IADC stores
// them as header datetimes), so they live in CRITICAL_PATH_MILESTONES, not here.
//
// CRITICAL_PATH_MAIN is the DRILLING-SPINE subset of the 39-op library — the
// code1s that ride the central lane of a drilling job. It expands the old
// 11-op starter list (adds CORING) and every member now sources its label from
// OPERATION_CODES. Workover/completion/intervention/P&A ops are NOT on this
// spine picklist by design; they surface per job class via operationsForPhase().
const CRITICAL_PATH_SPINE_CODE1: readonly string[] = [
  "DRILLING",
  "CORING",
  "REAMING",
  "CIRC_COND",
  "TRIPS",
  "RUN_CASING",
  "WIRELINE_LOGS",
  "FISHING",
  "WELL_CONTROL",
  "SVC_RIG",
  "REPAIR_RIG",
  "NPT",
];

export const CRITICAL_PATH_MAIN: readonly CodeOption[] =
  CRITICAL_PATH_SPINE_CODE1.map((code) => ({
    value: code,
    label: OPERATION_BY_CODE.get(code)?.label ?? code,
  }));

// Composite-keyed (code1 → subs) sub-code map for the spine ops. Sourced from
// OPERATION_CODES; NPT keeps the quick downtime-reason list.
export const CRITICAL_PATH_SUB: Record<string, readonly CodeOption[]> =
  CRITICAL_PATH_SPINE_CODE1.reduce<Record<string, readonly CodeOption[]>>(
    (acc, code) => {
      acc[code] =
        code === "NPT"
          ? NPT_CODE2_OPTIONS // NPT's sub-codes ARE the NPT downtime types
          : (OPERATION_BY_CODE.get(code)?.subCodes ?? []);
      return acc;
    },
    {},
  );

// Milestones are header datetimes (IADC/WITSML), NOT code1→code2 ops. The
// drilling four plus the completion/P&A additions the panel ratified
// (canonMigrationNotes §6 / openQuestionsForPO §7).
export const CRITICAL_PATH_MILESTONES: readonly CodeOption[] = [
  { value: "SPUD", label: "Spud" },
  { value: "CASING_POINT", label: "Casing Point" },
  { value: "TD_REACHED", label: "TD Reached" },
  { value: "RIG_RELEASE", label: "Rig Release" },
  { value: "COMPLETION_LANDED", label: "Completion Landed" },
  { value: "WELL_ON_PRODUCTION", label: "Well on Production" },
  { value: "WELL_SECURED", label: "Well Secured" },
  { value: "WELL_ABANDONED", label: "Well Abandoned" },
];

/** Codes available on every lane regardless of its specific list. */
export const UNIVERSAL_TASKS: readonly CodeOption[] = [
  { value: "WAIT", label: "Waiting On" },
  { value: "NPT", label: "NPT (non-productive time)" },
  { value: "SAFETY", label: "Safety / HSE" },
];

export const LANE_TASK_OPTIONS: Record<string, readonly CodeOption[]> = {
  "lane-mob": MOBILIZATION_TASKS,
  "lane-inspections": INSPECTION_TASKS,
  "lane-zonal": ZONAL_ISOLATION_TASKS,
  "lane-bop": BOP_TESTING_TASKS,
  "lane-mwd": MWD_TASKS,
  "lane-critical": CRITICAL_PATH_MAIN,
};

export const LANE_MILESTONE_OPTIONS: Record<string, readonly CodeOption[]> = {
  "lane-mob": MOBILIZATION_MILESTONES,
  "lane-zonal": ZONAL_ISOLATION_MILESTONES,
  "lane-bop": BOP_TESTING_MILESTONES,
  "lane-mwd": MWD_MILESTONES,
  "lane-critical": CRITICAL_PATH_MILESTONES,
};

/** Task / main-code picklist for a lane: its specific list + universals (deduped
 *  by value), else the global set. */
export function tasksForLane(laneId: string): readonly CodeOption[] {
  const specific = LANE_TASK_OPTIONS[laneId];
  if (!specific) return CODE1_OPTIONS;
  const seen = new Set(specific.map((o) => o.value));
  return [...specific, ...UNIVERSAL_TASKS.filter((u) => !seen.has(u.value))];
}

/** Lanes with a two-level main→sub model (others use a free-text sub). */
export const LANE_SUB_OPTIONS: Record<
  string,
  Record<string, readonly CodeOption[]>
> = {
  "lane-critical": CRITICAL_PATH_SUB,
  "lane-inspections": INSPECTION_SUB,
};

/** Sub-code options for a (lane, main-code) pair, or null for a free-text sub.
 *  NPT (any lane) → the NPT-type list; lanes in LANE_SUB_OPTIONS → their map. */
export function subCodesFor(
  laneId: string,
  code1: string,
): readonly CodeOption[] | null {
  if (code1 === "NPT") return NPT_CODE2_OPTIONS;
  const subs = LANE_SUB_OPTIONS[laneId]?.[code1];
  return subs && subs.length > 0 ? subs : null;
}

/** Milestone picklist for a lane (empty ⇒ free-text milestone name). */
export function milestonesForLane(laneId: string): readonly CodeOption[] {
  return LANE_MILESTONE_OPTIONS[laneId] ?? [];
}

// ── Reports (PO 2026-06-27) ─────────────────────────────────────────────────
// A Report entry is a zero-duration event (gray T-beam) that LAUNCHES an existing
// report screen. code1 = report family, code2 = sub-report. Only TIME-BASED
// reports that belong on the timeline narrative are listed. Cost Log is now included (PO 2026-07-28,
// reversing the earlier exclusion) — selecting it opens the bore Cost Log's in-page day modal on the
// clicked day (create-if-missing). Bore-property / depth-driven reports remain excluded. Every family
// maps to a live route; the start time is passed as ?at= for the report to seed from (the join
// handshake). NOT lane-driven.
// DERIVED, NOT HAND-MAINTAINED (PO 2026-08-27). This list used to be typed out by
// hand and had drifted badly: it carried a geothermal-era selection (loop pressure
// test, slurry disposal, formations) while CASING, drilling tools, drilling
// parameters and RigSigma — the core O&G operations — could not be opened from a
// timeline entry at all. 12 of the 21 job report types were missing.
//
// It is now derived from JOB_REPORT_TYPES minus an EXPLICIT exclusion list, so a
// new report type appears on the timeline by default and every omission is a named
// decision rather than an oversight. That inverts the failure mode: previously
// forgetting this file was silent, now excluding a type is the thing you must
// write down.
//
// WHY THIS MATTERS BEYOND THE TIMELINE: a report type needs registering in FOUR
// places (JOB_REPORT_TYPES, reportPagePath, ReportEditModal.renderReportForm, and
// here) and none of them fails a build when missed. RigSigma was routed, tested
// and reviewed while invisible in the sidebar for exactly this reason. This
// removes one of the four.
//
// NOT extended to features/stimulation/constants.ts, which keeps its own 9-entry
// frac-specific list. That one is a deliberate specialisation (Phase-3 screens
// pending, gated by isReportLive), not drift.

/** Job report types deliberately kept OFF the drilling timeline, each with its
 *  reason. Anything not listed here appears automatically. */
const TIMELINE_EXCLUDED_REPORTS: Readonly<Record<string, string>> = {
  // The timeline IS the time log — a Report entry launching it would be circular.
  "time-log": "the timeline is the time log",
  // No page exists: rendered inline in ReportEditModal only (NO_PAGE_REPORT_TYPES).
  "thermal-gradient": "modal-only, no page to launch",
  // Subtype picker with no landable page; the remaining subtypes are coming-soon.
  "pressure-test": "no page — subtype picker only",
};

/** Legacy `code1` values persisted on existing timeline entries, mapped to their
 *  canonical JOB_REPORT_TYPES value.
 *
 *  LOAD-BEARING: entries written before 2026-08-27 stored the timeline's own
 *  vocabulary. Deriving the list switches new entries to canonical values, so
 *  without this map every historical Report entry would stop resolving a label
 *  and a route — silent data rot on records that are not re-editable. */
export const LEGACY_REPORT_CODE: Readonly<Record<string, string>> = {
  "hse-safety": "safety",
  "hse-well-control": "well-control",
  "hse-drilling-hazards": "drilling-hazards",
  "hse-problem": "problem",
  "slurry-disposal": "solids-disposal",
  formations: "mud-log",
};

/** Normalise a stored code1 to its canonical JOB_REPORT_TYPES value. */
export function canonicalReportCode(code1: string): string {
  return LEGACY_REPORT_CODE[code1] ?? code1;
}

/** Timeline-launchable reports that are NOT job report types, with their route.
 *
 *  `survey` is the standing case: it has a live page (`/surveys`) and has always
 *  been on the timeline, but it is not in JOB_REPORT_TYPES — the sidebar likewise
 *  carries it as an `extraItems` special case. Deriving purely from
 *  JOB_REPORT_TYPES silently DROPPED it, which is the same class of loss this
 *  refactor exists to stop, so extras are explicit and additive rather than
 *  implied. */
const TIMELINE_EXTRA_REPORTS: readonly (CodeOption & { path: string })[] = [
  { value: "survey", label: "Survey", path: "/surveys" },
];

export const REPORT_TYPES: readonly CodeOption[] = [
  ...JOB_REPORT_TYPES.filter(
    (t) => !(t.value in TIMELINE_EXCLUDED_REPORTS),
  ).map((t) => ({ value: t.value, label: t.label })),
  ...TIMELINE_EXTRA_REPORTS.map((t) => ({ value: t.value, label: t.label })),
];

/** Report sub-reports (code2). Only the two route-per-subtype screens have them;
 *  the rest are single forms (free of a sub-report). */
// Keyed by CANONICAL code (see canonicalReportCode) now that REPORT_TYPES is
// derived. reportSubCodesFor normalises, so a persisted "hse-safety" still resolves.
export const REPORT_SUB: Record<string, readonly CodeOption[]> = {
  safety: [
    { value: "observation", label: "Observation" },
    { value: "near-miss", label: "Near Miss" },
    { value: "property-damage", label: "Property Damage" },
    { value: "spill-leak", label: "Spill / Leak" },
    { value: "injury", label: "Injury" },
  ],
  "well-control": [
    { value: "active", label: "Active" },
    { value: "passive", label: "Passive" },
    { value: "kill-sheet", label: "Kill Sheet" },
  ],
};

/** Sub-report options for a report family, or null when it's a single form.
 *  Accepts legacy codes (`hse-safety`) as well as canonical (`safety`). */
export function reportSubCodesFor(code1: string): readonly CodeOption[] | null {
  const subs = REPORT_SUB[canonicalReportCode(code1)];
  return subs && subs.length > 0 ? subs : null;
}

export interface ReportLaunchContext {
  borefieldId: string;
  boreId: string;
  jobId: string;
  startTs: string; // ISO — the timeline entry's start; the "join" time
}

/** Build the launch URL for a report family (+ sub-report), carrying the job/bore
 *  context and the timeline start time (?at=) for the report to consume.
 *  Returns null for an unknown family. */
export function reportRouteFor(
  code1: string,
  code2: string,
  ctx: ReportLaunchContext,
): string | null {
  // Cost Log opens the single-day Cost Log PAGE (/cost-log — one page per day, cost-log overhaul
  // PO 2026-08-05), carrying the well/job + ?at (the clicked day). useDailyReportPage reads ?at itself
  // and focuses that day, or scaffolds a blank one pre-dated to it (create-if-missing).
  if (code1 === "cost-log") {
    const p = new URLSearchParams({
      borefieldId: ctx.borefieldId,
      boreId: ctx.boreId,
      jobId: ctx.jobId,
      at: ctx.startTs,
    });
    return `/cost-log?${p.toString()}`;
  }
  // Route resolution is SHARED with the jobs reports list (features/jobs/reportRoute)
  // rather than kept in a local table. The local REPORT_ROUTE table it replaces had
  // drifted: it covered 9 families while 12 job report types had no timeline entry at
  // all, casing among them. resolveRoute also validates a subtype and falls back to
  // the family default, so a bad code2 cannot send the user to a screen that would
  // overwrite the loaded report's subType.
  const canon = canonicalReportCode(code1);
  const params = new URLSearchParams({
    borefieldId: ctx.borefieldId,
    boreId: ctx.boreId,
    jobId: ctx.jobId,
    at: ctx.startTs,
  });

  // Timeline-only extras (survey) carry their own path — they are not job report
  // types, so the shared resolver does not know them.
  const extra = TIMELINE_EXTRA_REPORTS.find((t) => t.value === canon);
  if (extra) return `${extra.path}?${params.toString()}`;

  const route = routeForReportTypeWithSub(canon, code2 || null);
  if (!route) return null;
  return `/${route}?${params.toString()}`;
}

// ── Attachment types (PO 2026-06-27) ────────────────────────────────────────
// ~30 main document/photo types captured per attachment alongside free-text
// keywords. Picklist-first (queryable) per the data-entry rules.
export const ATTACHMENT_TYPES: readonly CodeOption[] = [
  { value: "picture", label: "Picture / Photo" },
  { value: "document", label: "Document" },
  { value: "cement-report", label: "Cement Report" },
  { value: "bop-test-report", label: "BOP Test Report" },
  { value: "torque-turn-report", label: "Torque / Turn Report" },
  { value: "pipe-inspection", label: "Pipe Inspection" },
  { value: "casing-inspection", label: "Casing Inspection" },
  { value: "mud-report", label: "Mud Report" },
  { value: "mud-log", label: "Mud Log" },
  { value: "mwd-lwd-report", label: "MWD / LWD Report" },
  { value: "directional-survey", label: "Directional Survey" },
  { value: "wireline-log", label: "Wireline Log" },
  { value: "cement-bond-log", label: "Cement Bond Log (CBL)" },
  { value: "bit-record", label: "Bit Record / Bit Run" },
  { value: "daily-drilling-report", label: "Daily Drilling Report (DDR)" },
  { value: "casing-tally", label: "Casing Tally" },
  { value: "tally-sheet", label: "Tally Sheet" },
  { value: "pressure-test", label: "Pressure Test (FIT / LOT)" },
  { value: "geology-report", label: "Geology / Formation Report" },
  { value: "core-report", label: "Core Report" },
  { value: "gas-report", label: "Gas Report" },
  { value: "lab-analysis", label: "Lab Analysis" },
  { value: "calibration-cert", label: "Calibration Certificate" },
  { value: "inspection-cert", label: "Inspection Certificate" },
  { value: "delivery-ticket", label: "Delivery / Disposal Ticket" },
  { value: "waste-manifest", label: "Waste Manifest" },
  { value: "jsa", label: "JSA / Job Safety Analysis" },
  { value: "permit", label: "Work Permit" },
  { value: "incident-report", label: "Incident Report" },
  { value: "toolbox-talk", label: "Toolbox Talk / Pre-Tour" },
  { value: "schematic", label: "Diagram / Schematic" },
  { value: "video", label: "Video" },
  { value: "other", label: "Other" },
] as const;

/** Display label for an attachment type value (falls back to the raw value). */
export function attachmentTypeLabel(value: string): string {
  return ATTACHMENT_TYPES.find((o) => o.value === value)?.label ?? value;
}

/** Display label for a report family value (falls back to the raw value).
 *  Normalises legacy codes so Report entries written before 2026-08-27 still
 *  render a label instead of showing a raw `hse-safety` slug. */
export function reportLabel(code1: string): string {
  const canon = canonicalReportCode(code1);
  return REPORT_TYPES.find((o) => o.value === canon)?.label ?? code1;
}

/** Resolve a code's display label across all known task/milestone lists.
 *  Includes the full canonical OPERATION_CODES library. NOTE: this is a
 *  single-code (code2-only) fallback — because code2 is not globally unique it
 *  returns the FIRST match. For an exact label use the composite labelForOpCode. */
export function labelForCode(code: string): string {
  if (!code) return "";
  for (const list of [
    OPERATION_CODE1_OPTIONS,
    ...OPERATION_CODES.map((op) => op.subCodes),
    CODE1_OPTIONS,
    MOBILIZATION_TASKS,
    MOBILIZATION_MILESTONES,
    INSPECTION_TASKS,
    ...Object.values(INSPECTION_SUB),
    ZONAL_ISOLATION_TASKS,
    ZONAL_ISOLATION_MILESTONES,
    BOP_TESTING_TASKS,
    BOP_TESTING_MILESTONES,
    MWD_TASKS,
    MWD_MILESTONES,
    CRITICAL_PATH_MAIN,
    ...Object.values(CRITICAL_PATH_SUB),
    CRITICAL_PATH_MILESTONES,
    REPORT_TYPES,
    ...Object.values(REPORT_SUB),
  ]) {
    const hit = list.find((o) => o.value === code);
    if (hit) return hit.label;
  }
  return code;
}

// ── Color by primary code (theme-neutral, legible on light + dark) ──────────
export const CODE1_COLOR: Record<string, string> = {
  RIG_UP: "#6b7280", // gray — flat
  DRILL: "#2563eb", // blue — productive hole-making
  TRIP: "#0891b2", // cyan
  CIRC: "#64748b", // slate — flat
  SURVEY: "#7c3aed", // violet
  CASING: "#16a34a", // green — productive
  CEMENT: "#15803d", // dark green
  BOP: "#0d9488", // teal
  WELL_CTRL: "#b91c1c", // strong red — control event
  LOG: "#9333ea", // purple
  COMP: "#059669", // emerald
  RIG_DOWN: "#6b7280", // gray
  WAIT: "#94a3b8", // light slate — soft flat
  NPT: "#dc2626", // red — lost time
  SAFETY: "#ea580c", // orange
  WEATHER: "#d97706", // amber
};
export const DEFAULT_CODE_COLOR = "#64748b";

export function colorForCode1(code1: string): string {
  return CODE1_COLOR[code1] ?? DEFAULT_CODE_COLOR;
}

// ── Value-flow coloring of the CRITICAL PATH (PO 2026-06-27) ─────────────────
// The critical path reads as a value stream: value-ADDED work (raises the
// asset's resale value) is GREEN (drilling the darkest, others lighter);
// necessary-but-non-value work (should be parallelized or avoided) is GRAY;
// NPT / reaming / remedial work is RED. So the value flow of the well is legible
// straight off the spine.
export const VALUE_GREEN_DARK = "#15803d"; // drilling — the core value
export const VALUE_GREEN = "#16a34a"; // casing / run-casing
export const VALUE_GREEN_LIGHT = "#22c55e"; // logging / surveying
export const WASTE_GRAY = "#6b7280"; // necessary but non-value (trips, circ, svc)
export const REMEDIAL_RED = "#dc2626"; // NPT, reaming, repair, fishing, control

/** Critical-path code → value-flow color. Codes are the IADC CRITICAL_PATH_MAIN
 *  set. Unknown → gray (treated as necessary/neutral). */
export const CRITICAL_VALUE_COLOR: Record<string, string> = {
  // value-added (green; drilling darkest)
  DRILLING: VALUE_GREEN_DARK,
  RUN_CASING: VALUE_GREEN,
  WIRELINE_LOGS: VALUE_GREEN_LIGHT,
  SURVEY: VALUE_GREEN_LIGHT, // if surveying lands on the critical path
  // necessary but non-value (gray)
  TRIPS: WASTE_GRAY,
  CIRC_COND: WASTE_GRAY,
  SVC_RIG: WASTE_GRAY,
  WAIT: WASTE_GRAY,
  // NPT / remedial (red)
  NPT: REMEDIAL_RED,
  REAMING: REMEDIAL_RED,
  REPAIR_RIG: REMEDIAL_RED,
  FISHING: REMEDIAL_RED,
  WELL_CONTROL: REMEDIAL_RED,
  // Canon-library ops (canonMigrationNotes §3)
  CORING: VALUE_GREEN, // value-added evaluation
  PERFORATING: VALUE_GREEN,
  STIMULATION: VALUE_GREEN,
  ARTIFICIAL_LIFT: VALUE_GREEN,
  RUN_COMPLETION: VALUE_GREEN,
  SAND_CONTROL: VALUE_GREEN,
  SURFACE_HOOKUP: VALUE_GREEN_LIGHT,
  RIG_MOVE: WASTE_GRAY,
  TUBULAR_HANDLING: WASTE_GRAY,
  MUD_FLUIDS: WASTE_GRAY,
  INSPECTION: WASTE_GRAY,
  PLUG_ABANDON: REMEDIAL_RED,
  MILLING: REMEDIAL_RED,
  WELL_CLEANOUT: REMEDIAL_RED,
};

export function criticalPathColor(code1: string): string {
  return CRITICAL_VALUE_COLOR[code1] ?? WASTE_GRAY;
}

/** The value-flow legend for the critical path. */
export const VALUE_FLOW_LEGEND: readonly { label: string; color: string }[] = [
  { label: "Value-add", color: VALUE_GREEN_DARK },
  { label: "Necessary", color: WASTE_GRAY },
  { label: "NPT / Remedial", color: REMEDIAL_RED },
];

// ── Per-lane bar color for OFF-critical-path lanes (PO 2026-06-27) ───────────
// Each off-path lane has ONE identifying color (its activity bars all share it).
// Weather is handled separately as a single blue family (see weather.weatherColor).
// Zonal Isolation is gray + a cement-like pattern (see .tl-block--cement in CSS).
export const LANE_COLOR: Record<string, string> = {
  "lane-mob": "#d97706", // amber
  "lane-mwd": "#9333ea", // purple
  "lane-zonal": "#6b7280", // gray (+ cement pattern)
  "lane-bop": "#ec4899", // pink
  "lane-inspections": "#6b7280", // gray
  // New canonical lanes introduced by the multi-paradigm (prebake) time-logs —
  // without an entry each rendered uniform default gray. Distinct, theme-neutral.
  "lane-mud": "#ca8a04", // ochre — mud / fluids
  "lane-logs": "#7c3aed", // violet — wireline / logs
  "lane-completion": "#059669", // emerald — completion
  "lane-workover": "#0ea5e9", // sky — workover
  "lane-intervention": "#c026d3", // fuchsia — intervention
  "lane-pna": "#78716c", // stone — plug & abandon
  "lane-safety": "#ea580c", // orange — safety / HSE
  "lane-npt": "#dc2626", // red — non-productive time
  "lane-universal": "#64748b", // slate — general / other
};

export function laneColor(laneId: string): string | undefined {
  return LANE_COLOR[laneId];
}

/** Lane id whose bars carry the cement-like pattern overlay. */
export const CEMENT_PATTERN_LANE_ID = "lane-zonal";

/** localStorage key prefix for the demo persistence path (keyed by jobId).
 *  Bump the version when the seed/lane/model shape changes so cached demo data
 *  doesn't mask the new defaults. */
// v8: entryKind discriminant (replaces isMilestone) + per-entry attachments.
export const TIMELINE_STORAGE_PREFIX = "oxbo-timeline-v8:";
